import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useChurchPlanner } from '../../hooks/useChurchPlanner';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import { hasAnyPermission, Permission } from '../../utils/roles';
import {
  PlannerPage,
  PlannerCard,
  PlannerKicker,
  PlannerHeading,
  PlannerSubtle,
  PlannerError,
  plannerColors,
} from './plannerTheme';
import { formatPlannerStatusLabel, normalizePlannerStatus } from './plannerStatus';

const SectionTitle = styled.h3`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 1rem;
  font-weight: 700;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const Item = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  padding: 0.85rem 0.95rem;
  background: ${plannerColors.surfaceHover};
`;

const ActionLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem 0.7rem;
  border-radius: 8px;
  border: 1px solid ${plannerColors.border};
  color: ${plannerColors.text};
  text-decoration: none;
  font-size: 0.8rem;
  font-weight: 600;
  margin-right: 0.5rem;

  &:hover {
    border-color: ${plannerColors.accent};
    color: ${plannerColors.accent};
  }
`;

function formatDateTime(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getRoleName(entryRole: { name: string } | Array<{ name: string }> | null | undefined) {
  if (!entryRole) return null;
  if (Array.isArray(entryRole)) return entryRole[0]?.name ?? null;
  return entryRole.name ?? null;
}

export default function ChurchPlannerProfilePage() {
  const { user } = useAuth();
  const planner = useChurchPlanner(true, user?.id);
  const { data: userProfile } = useCurrentUserProfile(user?.id);
  const profile = planner.currentUserQuery.data;
  const roles = userProfile?.roles ?? user?.user_metadata?.roles;
  const canManagePlanner = hasAnyPermission(roles, [
    Permission.CREATE_CHURCH_PLAN,
    Permission.MANAGE_CHURCH_PLANNER_ROLES,
    Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS,
  ]);

  const openAssignments = planner.plans.reduce((total, plan) => {
    const unfilled = (plan.service_plan_assignments || []).filter((entry) => !entry.user_id).length;
    return total + unfilled;
  }, 0);

  return (
    <PlannerPage>
      <PlannerCard>
        <PlannerKicker>Account</PlannerKicker>
        <PlannerHeading>Profile</PlannerHeading>
        {planner.currentUserQuery.isLoading ? (
          <PlannerSubtle>Loading profile…</PlannerSubtle>
        ) : (
          <>
            <PlannerSubtle style={{ marginTop: '0.5rem' }}>
              Name: {profile?.full_name || user?.email || 'Unknown user'}
            </PlannerSubtle>
            <PlannerSubtle>Email: {profile?.email || user?.email || 'No email available'}</PlannerSubtle>
            <PlannerSubtle>Title: {profile?.title || 'Not set'}</PlannerSubtle>
            <PlannerSubtle>Phone: {profile?.phone || 'Not set'}</PlannerSubtle>
          </>
        )}
        {planner.isError ? <PlannerError>Unable to load profile planner data right now.</PlannerError> : null}
      </PlannerCard>

      {canManagePlanner ? (
        <PlannerCard>
          <SectionTitle>Planner admin summary</SectionTitle>
          <PlannerSubtle>Total plans: {planner.plans.length}</PlannerSubtle>
          <PlannerSubtle>Upcoming plans: {planner.upcomingPlans.length}</PlannerSubtle>
          <PlannerSubtle>Unfilled assignment slots: {openAssignments}</PlannerSubtle>
          <div style={{ marginTop: '0.75rem' }}>
            <ActionLink to="/church-planner/settings">Planner Admin</ActionLink>
            <ActionLink to="/church-planner/my-assignments">My Assignments</ActionLink>
          </div>
        </PlannerCard>
      ) : null}

      <PlannerCard>
        <SectionTitle>Upcoming services</SectionTitle>
        {planner.isLoading ? (
          <PlannerSubtle>Loading assignments…</PlannerSubtle>
        ) : planner.myUpcomingAssignments.length === 0 ? (
          <PlannerSubtle>No upcoming assignments found.</PlannerSubtle>
        ) : (
          <List>
            {planner.myUpcomingAssignments.map((entry) => (
              <Item key={entry.assignment.id}>
                <PlannerSubtle style={{ margin: 0 }}>{entry.plan.title}</PlannerSubtle>
                <PlannerSubtle>{formatDateTime(entry.plan.service_date)}</PlannerSubtle>
                <PlannerSubtle>
                  Role: {getRoleName(entry.assignment.service_plan_roles) || 'Unassigned role'}
                </PlannerSubtle>
                <PlannerSubtle>Status: {formatPlannerStatusLabel(normalizePlannerStatus(entry.assignment.status))}</PlannerSubtle>
              </Item>
            ))}
          </List>
        )}
      </PlannerCard>

      <PlannerCard>
        <SectionTitle>Serving history</SectionTitle>
        {planner.isLoading ? (
          <PlannerSubtle>Loading history…</PlannerSubtle>
        ) : planner.myHistoryAssignments.length === 0 ? (
          <PlannerSubtle>No service history found.</PlannerSubtle>
        ) : (
          <List>
            {planner.myHistoryAssignments.slice(0, 12).map((entry) => (
              <Item key={entry.assignment.id}>
                <PlannerSubtle style={{ margin: 0 }}>{entry.plan.title}</PlannerSubtle>
                <PlannerSubtle>{formatDateTime(entry.plan.service_date)}</PlannerSubtle>
                <PlannerSubtle>
                  Role: {getRoleName(entry.assignment.service_plan_roles) || 'Unassigned role'}
                </PlannerSubtle>
                <PlannerSubtle>Status: {formatPlannerStatusLabel(normalizePlannerStatus(entry.assignment.status))}</PlannerSubtle>
              </Item>
            ))}
          </List>
        )}
      </PlannerCard>
    </PlannerPage>
  );
}
